const parseReturn = (commission, currency) => {
  if (currency) {
    return { amount: commission, currency };
  }
  return commission;
};

/**
 * Finds the applicable commission rule using binary search
 * @param {Array} sortedRules - Commission rules sorted by threshold (ascending)
 * @param {number} amount - The accumulated amount to find rule for
 * @returns {Object|null} The applicable rule or null if none found
 */

const applyCommissionRule = (rule, amount) => {
  let commission = rule.initialPercentage * amount;
  if (rule.minValue !== null && !Number.isNaN(rule.minValue)) {
    commission = Math.max(commission, rule.minValue);
  }
  if (rule.maxValue !== null && !Number.isNaN(rule.maxValue)) {
    commission = Math.min(commission, rule.maxValue);
  }
  return commission;
};

const findApplicableRule = (sortedRules, amount) => {
  if (!sortedRules || sortedRules.length === 0) return null;

  // Binary search for the lower threshold that is > amount
  let left = 0;
  let right = sortedRules.length - 1;
  let result = sortedRules[sortedRules.length - 1];
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const rule = sortedRules[mid];

    if (rule.threshold > amount) {
      result = rule; // This could be our answer
      right = mid - 1; // But check if there's a lower threshold that still qualifies
    } else {
      left = mid + 1; // This threshold is too low, search higher
    }
  }

  return result;
};

export const calculateCommission = (commissionRules, accumulatedAmountWithIva) => {
  const amount = accumulatedAmountWithIva?.amount || accumulatedAmountWithIva;
  const currency = accumulatedAmountWithIva?.currency || null;

  if (!commissionRules || commissionRules.length === 0 || amount <= 0) {
    return parseReturn(0, currency);
  }

  const sortedRules = [...commissionRules].sort((a, b) => a.threshold - b.threshold);
  const applicableRule = findApplicableRule(sortedRules, amount);

  if (!applicableRule) {
    return parseReturn(0, currency);
  }

  const commission = applyCommissionRule(applicableRule, amount);
  return parseReturn(commission, currency);
};

export const calculateInvoiceAmounts = (invoiceData) => {
  const { days, retentionRate, monthlyRate,
    amountWithIva: { amount: amountWithIva } } = invoiceData;
  const effectiveRate = (monthlyRate / 100) * (days / 30);
  const retentionAmount = Math.round((retentionRate / 100) * amountWithIva);
  const advanceAmount = amountWithIva - retentionAmount;
  const priceDifference = Math.round(advanceAmount * effectiveRate);
  const amountMinusInterests = advanceAmount - priceDifference;
  return { effectiveRate, retentionAmount, advanceAmount, amountMinusInterests, priceDifference };
};
